import { action, observable, runInAction } from "mobx";
import { IDispatch, IDispatchDetail, IDispatchHistory, IDispatchList, IDispatchStatus, IDispatchTicketList, IDispatchTruck, IDispatchUpdate } from "src/Interfaces/DispatchInterface";
import { API } from "src/Services/Api/Api";
import PdfService from "src/Services/Pdf";
import { RootStore } from "../Root/RootStore";

class DispatchApi {
    private api: API;
    constructor(api: API) {
        this.api = api;
    }

    public getDispatches() {
        return this.api.get('/dispatch');
    }

    public getDispatch(dispatchId: string) {
        return this.api.get(`/dispatch/${dispatchId}`);
    }

    public postDispatch(dispatch: IDispatch) {
        return this.api.post('/dispatch', dispatch)
        .then(
            (data: any) => data,
            (error: any) => { throw error; }
        );
    }

    public putDispatch(dispatch: IDispatchUpdate) {
        return this.api.put(`/dispatch/${dispatch.id}`, dispatch);
    }

    public updateDispatchStatus(dispatchId: string, dispatchStatus: IDispatchStatus) {
        return this.api.put(`/dispatch/${dispatchId}/status`, dispatchStatus);
    }

    public setTruckCancelled(truckId: string) {
        return this.api.put(`/dispatch/truck/${truckId}/cancelled`, {});
    }

    public updateDispatchTruckAfterConfirm(dispatchTruck: IDispatchTruck) {
        return this.api.put(`/dispatch/truck/${dispatchTruck.id}`, dispatchTruck);
    }

    public deleteConfirmedTruck(dispatchTruck: IDispatchTruck) {
        return this.api.delete(`/dispatch/truck/${dispatchTruck.id}`);
    }

    public setTruckEnRoute(truckId: string) {
        return this.api.post(`/dispatch/truck/${truckId}/EnRoute`, {});
    }

    public setTransferTruckEnRoute(dispatchTruck: IDispatchTruck, wellId: string) {
        return this.api.post(`/dispatch/truck/${dispatchTruck.id}/Transfer/EnRoute/${wellId}`, dispatchTruck, wellId);
    }

    public setTruckCompleted(truckId: string) {
        return this.api.post(`/dispatch/truck/${truckId}/completed`, {});
    }

    public addComment(comment: string, dispatchId: string) {
        return this.api.post(`/dispatch/${dispatchId}/comment`, { comment });
    }

    public getTruckTicketPickup(truckId: string) {
        return this.api.get(`/dispatch/truckticket/${truckId}/pickup`);
    }

    public getTruckTicketDelivery(truckId: string) {
        return this.api.get(`/dispatch/truckticket/${truckId}/delivery`);
    }
}

export class DispatchStore {
    @observable
    public savingDispatch: boolean = false;
    public RootStore: RootStore;
    public api: DispatchApi;

    @observable
    public dispatches: IDispatchList[];

    @observable
    public dispatch: IDispatchDetail;

    constructor(rootStore: RootStore) {
        this.RootStore = rootStore;
        this.api = new DispatchApi(rootStore.Api);
    }

    public async getDispatches() {
        const dispatches = await this.api.getDispatches();
        if (dispatches) {
            runInAction(() => {
                this.dispatches = dispatches;
            });
            return dispatches;
        }
    }

    public async getDispatch(dispatchId: string) {
        const dispatch = await this.api.getDispatch(dispatchId);
        if (dispatch) {
            runInAction(() => {
                this.dispatch = dispatch;
            });
            return dispatch;
        }
    }

    public getDispatchType(dispatch: IDispatchHistory | undefined) {
        if (!dispatch) {
            return '';
        }
        return dispatch.dispatchHeader.type;
    }
    
    @action
    public postDispatch = async (dispatch: IDispatch ) => {
        if (this.savingDispatch) {
            return;
        }
        this.savingDispatch = true;
        try {
            const dispatchResult = await this.api.postDispatch(dispatch);
            return dispatchResult;
        } finally {
            this.savingDispatch = false;
        }
    }

    public putDispatch(dispatch: IDispatchUpdate) {
        return this.api.putDispatch(dispatch);
    }

    public updateDispatchTruckAfterConfirm(dispatchTruck: IDispatchTruck) {
        return this.api.updateDispatchTruckAfterConfirm(dispatchTruck);
    }

    public deleteConfirmedTruck(dispatchTruck: IDispatchTruck) {
        return this.api.deleteConfirmedTruck(dispatchTruck);
    }

    public updateDispatchStatus(dispatchId: string, dispatchStatus: IDispatchStatus) {
        return this.api.updateDispatchStatus(dispatchId, dispatchStatus);
    }
    public setTruckCancelled(truckId: string) {
        return this.api.setTruckCancelled(truckId);
    }
      

    public setTruckEnRoute(truckId: string) {
        return this.api.setTruckEnRoute(truckId);
    }

    public setTransferTruckEnRoute(dispatchTruck: IDispatchTruck, wellId: string) {
        return this.api.setTransferTruckEnRoute(dispatchTruck, wellId);
    }

    public setTruckCompleted(truckId: string) {
        return this.api.setTruckCompleted(truckId);
    }

    public getTruckTicketDelivery(truckId: string) {
        return this.api.getTruckTicketDelivery(truckId);
    }

    public getTruckTicketPickup(truckId: string) {
        return this.api.getTruckTicketPickup(truckId);
    }

    public async getDispatchTicketForDownload(ticket: IDispatchTicketList) {

        if (ticket.type === "Delivery") {
        return this.api.getTruckTicketDelivery(ticket.id)
            .then(res => PdfService.createDispatchTruckTicket(res))
            .then(blob => {
                // const file = PdfService.createDispatchTruckTicket(blob)
                const link = document.createElement('a');
                link.href = blob;
                link.setAttribute('download', ticket.type);  // 3. Append to html page
                document.body.appendChild(link);  // 4. Force download
                link.click();  // 5. Clean up and remove the link
                if (link.parentNode) {
                    link.parentNode.removeChild(link);
                }
            })
            .catch(error => {
                this.RootStore.MessageStore.addMessage(error, 'danger');
            });
        } else {
            return this.api.getTruckTicketPickup(ticket.id)
            .then(res => PdfService.createDispatchTruckTicket(res))
            .then(blob => {
                const link = document.createElement('a');
                link.href = blob;
                link.setAttribute('download', ticket.type);  // 3. Append to html page
                document.body.appendChild(link);  // 4. Force download
                link.click();  // 5. Clean up and remove the link
                if (link.parentNode) {
                    link.parentNode.removeChild(link);
                }
            })
            .catch(error => {
                this.RootStore.MessageStore.addMessage(error, 'danger');
            });
        }
    }

    public async addComment(comment: string) {
        const retComment = await this.api.addComment(comment, this.dispatch.id);
        runInAction(() => {
            this.dispatch.comments.unshift(retComment);
        });
        return retComment;
    }
}


// WEBPACK FOOTER //
// ./src/Stores/Dispatch/DispatchStore.ts